import { useCallback, useState } from 'react';
import { AxiosResponse } from 'axios';
import { isAxiosError } from '../../core/api/client/secureAxios';

export interface UseMutateParams<TData, TResponse, TError> {
    method: (url: string, data?: TData) => Promise<AxiosResponse<TResponse>>;
    onSuccess?: (response: TResponse) => void;
    onError?: (errorResponse: TError) => void;
}

export const useMutate = <TData, TResponse = unknown, TError = unknown>({
    method,
    onSuccess,
    onError
}: UseMutateParams<TData, TResponse, TError>) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>();

    const mutate = useCallback(
        async (url: string, data?: TData) => {
            setLoading(true);
            setError(undefined);

            try {
                const response = await method(url, data);
                onSuccess?.(response.data);
                return response;
            } catch (error: unknown) {
                setError(error);
                if (isAxiosError<TError>(error) && error.response) {
                    onError?.(error.response.data);
                }
            } finally {
                setLoading(false);
            }
        },
        [method, onError, onSuccess]
    );

    return {
        mutate,
        loading,
        error
    };
};
